/**
 * Die Werkzeuge ueber der Karte.
 *
 * Zwei Zustaende, nicht einer:
 *
 *   Normal zeigt nur, was gerade etwas nuetzt. Ohne Flaeche gibt es nichts
 *   auszusparen und keine Traufkante zu waehlen; ohne Module nichts zu
 *   verstringen. Vorher standen alle vierzehn Knoepfe immer da, die Haelfte
 *   davon grau, und der Anfang — "Gebäude auswählen", "Fläche zeichnen" —
 *   ging darin unter.
 *
 *   Waehrend gezeichnet oder eine Flaeche bearbeitet wird, verschwindet alles
 *   andere. Dann zaehlt nur: einen Schritt zurueck, einen vor, fertig oder
 *   abbrechen. Dieses Menue sitzt unten in der Karte, in Daumennaehe — am
 *   Tablet ist oben links die unbequemste Ecke.
 */

import { useLayoutEffect, useRef } from 'react';

import type { Kartenmodus } from './Karte.tsx';

/**
 * Wie weit die Leiste seitlich gewischt war. Während einer Arbeit ist sie
 * nicht da und wird danach neu aufgebaut — am iPad stand sie dann jedes Mal
 * wieder am Anfang, und „Hindernis“ musste neu gesucht werden.
 */
let gemerkterVersatz = 0;

export interface Werkzeugstand {
  modus: Kartenmodus;
  /** Gibt es ueberhaupt schon eine Flaeche? */
  hatFlaechen: boolean;
  /** Ist gerade eine Flaeche gewaehlt, ist es ein Dach, und liegen Module darauf? */
  aktiveFlaeche: { istDach: boolean; hatModule: boolean } | null;
}

export interface Werkzeuggriffe {
  setModus: (modus: Kartenmodus) => void;
  onZurueck: () => void;
  onGelaende: () => void;
}

const MODUSNAMEN: Partial<Record<Kartenmodus, string>> = {
  zeichnen: 'Fläche zeichnen',
  aussparung: 'Hindernis zeichnen',
  modulfeld: 'Modulfeld zeichnen',
  fassade: 'Fassade: Wandlinie ziehen',
  'verschattung-zeichnen': 'Verschattungsobjekt umfahren',
  traufkante: 'Traufkante wählen',
  bearbeiten: 'Fläche bearbeiten',
  umrisse: 'Gebäudeumrisse ausrichten',
  module: 'Module an/aus',
  bemassung: 'Bemaßung',
  markierung: 'Markierung mit Pfeil',
};

const MODUSHINWEISE: Partial<Record<Kartenmodus, string>> = {
  zeichnen: 'Ecken der Dachfläche antippen. Rechte Winkel rasten ein.',
  aussparung: 'Kamin, Dachfenster oder Gaube grob umfahren — das genaue Maß kommt rechts.',
  modulfeld:
    'Mindestens vier Ecken auf der Dachfläche antippen. Die Module kommen mittig ins Feld, die Randabstände zum Dach gelten weiter.',
  fassade: 'Zwei Punkte: Anfang und Ende der Wand. Höhe kommt danach.',
  'verschattung-zeichnen': 'Baum oder Nachbargebäude umfahren.',
  bearbeiten:
    'Ecke ziehen. Innen ziehen verschiebt die ganze Fläche. Ein Tipp auf die Kantenmitte setzt eine neue Ecke.',
  module:
    'Tipp auf ein Modul nimmt es heraus. Tipp auf den gestrichelten Platz setzt es wieder.',
  bemassung: 'Anfang und Ende antippen. Ecken von Flächen und Hindernissen rasten ein.',
  markierung: 'Erst die Pfeilspitze — wo das Gerät sitzt —, dann den Ort der Beschriftung.',
  umrisse:
    'Die Umrisse gemeinsam auf die Dächer ziehen. Gleicht den Dachvorsprung aus, der überall gleich ist — sitzt ein einzelnes Haus daneben, richtet man das je Fläche mit „Fläche bearbeiten“.',
};

/** Das Menue waehrend einer Arbeit — nichts sonst. */
export function Arbeitsmenue({
  modus,
  anzahl,
  genug,
  fehlend,
  kannZurueck,
  kannVor,
  gewaehlteEcke,
  onZurueck,
  onVor,
  onEckeEntfernen,
  onFertig,
  onAbbrechen,
  entfernteModule,
  onAlleAktivieren,
  zusatz,
  titel,
}: {
  modus: Kartenmodus;
  /** Statt des Modusnamens — „Fläche bearbeiten“ passt nicht, wenn ein Hindernis gezogen wird. */
  titel?: string;
  anzahl: number;
  genug: boolean;
  /** Was noch fehlt, im Klartext — statt eines grauen Knopfes ohne Begruendung. */
  fehlend: string | null;
  kannZurueck: boolean;
  kannVor: boolean;
  gewaehlteEcke: number | null;
  onZurueck: () => void;
  onVor: () => void;
  onEckeEntfernen: () => void;
  onFertig: () => void;
  onAbbrechen: () => void;
  /** Nur im Modus "module": wie viele herausgenommen sind, und alle zurueck. */
  entfernteModule?: number;
  onAlleAktivieren?: () => void;
  /** Eingaben, die zur Arbeit gehören — etwa Art und Text einer Markierung. */
  zusatz?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="arbeitsmenue">
      <div className="arbeitsmenue__kopf">
        <strong>{titel ?? MODUSNAMEN[modus] ?? 'Zeichnen'}</strong>
        <span className="klein">
          {fehlend ?? MODUSHINWEISE[modus] ?? ''}
          {modus !== 'bearbeiten' && modus !== 'module' && anzahl > 0 ? ` · ${anzahl} Punkt(e)` : ''}
        </span>
      </div>

      {zusatz && <div className="arbeitsmenue__zusatz">{zusatz}</div>}

      <div className="arbeitsmenue__knoepfe">
        <button onClick={onZurueck} disabled={!kannZurueck} title="Einen Schritt zurück">
          ↶ Zurück
        </button>
        <button onClick={onVor} disabled={!kannVor} title="Wieder vorwärts">
          ↷ Vor
        </button>

        {modus === 'bearbeiten' && (
          <button
            className="gefaehrlich"
            onClick={onEckeEntfernen}
            disabled={gewaehlteEcke === null}
            title={
              gewaehlteEcke === null ? 'Erst eine Ecke antippen' : `Ecke ${gewaehlteEcke + 1} weg`
            }
          >
            Ecke entfernen
          </button>
        )}

        {modus === 'module' && (
          <button onClick={onAlleAktivieren} disabled={!entfernteModule}>
            Alle wieder aktivieren{entfernteModule ? ` (${entfernteModule})` : ''}
          </button>
        )}

        <button className="wichtig gross" onClick={onFertig} disabled={!genug}>
          ✓ Fertig
        </button>
        {/* Beim An- und Abschalten ist jeder Tipp schon gespeichert — es gibt
            nichts abzubrechen, nur zurueckzunehmen. */}
        {modus !== 'module' && <button onClick={onAbbrechen}>Abbrechen</button>}
      </div>
    </div>
  );
}

/**
 * Die normale Leiste — gestuft nach dem, was schon da ist.
 *
 * Eine Reihe, kein Untermenue. "Mehr" war ein Klick zwischen dem Planer und
 * dem, was er sucht, und die Leiste ist kurz genug ohne.
 *
 * Nicht enthalten, obwohl es die Modi gibt:
 *
 *   **Traufkante** steht rechts im Flaechenpanel, direkt bei der Ausrichtung,
 *   auf die sie wirkt. Zweimal derselbe Knopf an zwei Orten heisst, dass man
 *   sich merken muss, welcher gemeint war.
 *
 *   **Verstringen** beginnt rechts im Anlagenpanel, indem man einen String
 *   waehlt — dort steht auch, welcher gerade offen ist. Ein Knopf hier oben
 *   waere ein zweiter Einstieg in dieselbe Arbeit.
 *
 *   **Die Kartenquelle.** Gearbeitet wird auf dem Orthofoto; die Grauversion
 *   war nie mehr als eine Orientierungshilfe. Ein Umschalter, den niemand
 *   braucht, ist eine Stelle, an der man versehentlich landet.
 */
export function Werkzeugleiste({
  stand,
  griffe,
  nurLesen = false,
}: {
  stand: Werkzeugstand;
  griffe: Werkzeuggriffe;
  /** Montage: ansehen und messen, nichts zeichnen. */
  nurLesen?: boolean;
}): React.JSX.Element {
  const { modus, aktiveFlaeche } = stand;
  const leiste = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (leiste.current) leiste.current.scrollLeft = gemerkterVersatz;
  }, []);
  const merke = (): void => {
    if (leiste.current) gemerkterVersatz = leiste.current.scrollLeft;
  };
  const umschalten = (ziel: Kartenmodus): void =>
    griffe.setModus(modus === ziel ? 'ansehen' : ziel);

  if (nurLesen) {
    return (
      <div className="werkzeugleiste">
        <button onClick={griffe.onZurueck}>Zurück</button>
        <span className="werkzeugleiste__trenner" />
        <button aria-pressed={modus === 'messen'} onClick={() => umschalten('messen')}>
          Messen
        </button>
      </div>
    );
  }

  return (
    <div className="werkzeugleiste" ref={leiste} onScroll={merke}>
      <button onClick={griffe.onZurueck}>Zurück</button>

      <span className="werkzeugleiste__trenner" />

      {/* Der Anfang. Steht immer vorne, weil hier jede Aufnahme beginnt. */}
      <button aria-pressed={modus === 'vorschlag'} onClick={() => umschalten('vorschlag')}>
        Gebäude auswählen
      </button>
      <button aria-pressed={modus === 'zeichnen'} onClick={() => umschalten('zeichnen')}>
        Fläche zeichnen
      </button>

      {/* Erst sinnvoll, wenn eine Fläche gewählt ist. */}
      {aktiveFlaeche && (
        <>
          <span className="werkzeugleiste__trenner" />
          <button aria-pressed={modus === 'bearbeiten'} onClick={() => umschalten('bearbeiten')}>
            Fläche bearbeiten
          </button>
          {aktiveFlaeche.istDach && (
            <button
              aria-pressed={modus === 'aussparung'}
              onClick={() => umschalten('aussparung')}
              title="Rauchfang, Dachfenster, Gaube — die Module weichen aus"
            >
              Hindernis
            </button>
          )}
          {aktiveFlaeche.istDach && aktiveFlaeche.hatModule && (
            <button
              aria-pressed={modus === 'module'}
              onClick={() => umschalten('module')}
              title="Einzelne Module herausnehmen oder wieder setzen"
            >
              Module an/aus
            </button>
          )}
        </>
      )}

      <span className="werkzeugleiste__trenner" />
      <button aria-pressed={modus === 'fassade'} onClick={() => umschalten('fassade')}>
        Fassade
      </button>
      <button aria-pressed={modus === 'verschattung-zeichnen'} onClick={() => umschalten('verschattung-zeichnen')}>
        Verschattung
      </button>
      <button
        onClick={griffe.onGelaende}
        title="Sucht Bäume und Nachbargebäude im Höhenmodell des BEV und legt sie als Verschattungsobjekte an. Braucht Höhendaten am Server."
      >
        Verschattung aus Gelände
      </button>

      <span className="werkzeugleiste__trenner" />
      <button aria-pressed={modus === 'messen'} onClick={() => umschalten('messen')}>
        Messen
      </button>
      <button
        aria-pressed={modus === 'bemassung'}
        onClick={() => umschalten('bemassung')}
        title="Ein Maß, das stehen bleibt — für den Plan und die Montage"
      >
        Bemaßung
      </button>
      <button
        aria-pressed={modus === 'markierung'}
        onClick={() => umschalten('markierung')}
        title="Wechselrichter, Verteiler oder Hinweis mit Pfeil einzeichnen"
      >
        Markierung
      </button>
      <button
        aria-pressed={modus === 'umrisse'}
        onClick={() => umschalten('umrisse')}
        title="Der Umriss ist der Grundriss am Boden, das Luftbild zeigt das Dach — hier einmal gemeinsam ausrichten"
      >
        Umrisse ausrichten
      </button>
    </div>
  );
}
